import { useMemo, useState, type ReactElement, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSessions, type SessionListItem } from '../../api/admin.js';
import { formatDurationMs, formatNumber, nanoToUsdCompact, formatDateTime, shortId } from '../../lib/format.js';
import { preferRawCost } from '../../lib/cost-display.js';
import { useUrlPaginatedQuery } from '../../lib/useUrlPaginatedQuery.js';
import { useUrlParam, useUrlParamWithStorage } from '../../lib/useUrlTab.js';
import { useLiveClock } from '../../lib/useLiveClock.js';

const MAX_COMPARE = 3;

// Tipos de módulo para el filtro. El `value` es el slug que se guarda en
// `voice_sessions.module_id` (= el moduleSlug del handshake WS); el backend
// hace match parcial (ILIKE %value%), así que "coach" también trae submódulos
// ("coach-ansiedad"). Lista canónica del inventario (06-modules-and-tools).
const MODULE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Todos los módulos' },
  { value: 'english', label: 'Inglés (Speak)' },
  { value: 'scout', label: 'Scout (entrevistas)' },
  { value: 'coach', label: 'Coach' },
  { value: 'videos', label: 'Videos' },
  { value: 'fonix', label: 'Fonix' },
  { value: 'general_assistant', label: 'Asistente general' },
];

export function SessionsListPage(): ReactElement {
  const navigate = useNavigate();
  // Filtros persistidos en la URL (F5 + share-link friendly). El cursor
  // queda en memoria — paginar es state efímero y aporta poco como link.
  const [errFlag, setErrFlag] = useUrlParam('err');
  const hasError = errFlag === '1';
  const [moduleParam, setModuleParam] = useUrlParam('module');
  const moduleFilter = moduleParam ?? '';
  // Fase 39 — toggle para incluir sesiones sintéticas del simulator.
  // Default OFF (los queries del backend filtran kind='real' sin esto).
  // Persistido en localStorage: si el user lo tildó antes y vuelve a
  // /sessions desde el sidebar (sin ?synthetic=1 en la URL), el filtro
  // sigue tildado en lugar de resetear a default.
  const [synFlag, setSynFlag] = useUrlParamWithStorage(
    'synthetic',
    'admin.sessions.includeSynthetic',
  );
  const includeSynthetic = synFlag === '1';
  // Fase 42 — filtro "solo en vivo" persistido en URL + localStorage. El
  // backend filtra por ended_at IS NULL cuando este flag está activo.
  const [liveFlag, setLiveFlag] = useUrlParamWithStorage(
    'live',
    'admin.sessions.onlyLive',
  );
  const onlyLive = liveFlag === '1';
  const pager = useUrlPaginatedQuery();
  const [compareIds, setCompareIds] = useState<string[]>([]);

  function openSession(id: string, e: MouseEvent<HTMLElement>): void {
    // Si el click bajó sobre un <a>, input/checkbox o button nested, dejar
    // que ese elemento maneje el evento.
    const target = e.target as HTMLElement;
    if (target.closest('a, input, button, label')) return;
    void navigate(`/sessions/${id}`);
  }

  function toggleCompare(sessionId: string): void {
    setCompareIds((prev) => {
      if (prev.includes(sessionId)) return prev.filter((id) => id !== sessionId);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, sessionId];
    });
  }

  const query = useQuery({
    queryKey: [
      'admin',
      'sessions',
      { hasError, moduleFilter, includeSynthetic, onlyLive, cursor: pager.cursor },
    ],
    queryFn: () => {
      const filters: Parameters<typeof fetchSessions>[0] = { hasError, limit: 50 };
      if (moduleFilter) filters.moduleId = moduleFilter;
      if (includeSynthetic) filters.includeSynthetic = true;
      if (onlyLive) filters.onlyLive = true;
      if (pager.cursor) filters.cursor = pager.cursor;
      return fetchSessions(filters);
    },
    // Fase 42 — refresh agresivo si hay live visibles. El backend devuelve
    // items["endedAt"] = null para sesiones en curso; si hay al menos una,
    // refetch cada 10s para que las que terminen actualicen su badge y las
    // nuevas aparezcan sin requerir F5.
    refetchInterval: (q): number | false => {
      if (onlyLive) return 10_000;
      const items = q.state.data?.items ?? [];
      return items.some((it: SessionListItem) => it.endedAt === null)
        ? 10_000
        : false;
    },
  });

  // Re-ordenamos client-side para que las live aparezcan primero dentro de
  // la página actual. El backend mantiene ORDER BY started_at DESC para no
  // romper la paginación por cursor (que también ordena por startedAt).
  // Sólo afecta la fila visible: 50 items es barato y la mayoría de las
  // live son recientes igual.
  const sortedItems = useMemo<SessionListItem[]>(() => {
    const items = query.data?.items;
    if (!items || items.length === 0) return [];
    const live = items.filter((i) => i.endedAt === null);
    const ended = items.filter((i) => i.endedAt !== null);
    return [...live, ...ended];
  }, [query.data]);

  const hasLiveVisible = sortedItems.some((it) => it.endedAt === null);
  // Sólo arrancamos el tick de 1s si hay alguna live a la vista — evita
  // re-render gratuito cuando todo lo que se ve está cerrado.
  const liveNow = useLiveClock(1000, hasLiveVisible);

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sessions</h1>
          <p className="page-sub">
            Lista paginada con filtros básicos.
            {query.data && typeof query.data.totalApprox === 'number' ? (
              <>
                {' '}· <span className="tt-tert mono">~{query.data.totalApprox.toLocaleString('es-AR')} totales</span>
              </>
            ) : null}
          </p>
        </div>
      </div>
      <div className="filterbar">
        <label className="group">
          <input
            type="checkbox"
            checked={hasError}
            onChange={(e) => {
              setErrFlag(e.target.checked ? '1' : null);
              pager.reset();
            }}
            data-testid="filter-has-error"
          />
          <span>solo con errores</span>
        </label>
        <label className="group">
          <span>módulo</span>
          <select
            value={moduleFilter}
            onChange={(e) => {
              setModuleParam(e.target.value || null);
              pager.reset();
            }}
            data-testid="filter-module"
          >
            {MODULE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="group">
          <input
            type="checkbox"
            checked={includeSynthetic}
            onChange={(e) => {
              setSynFlag(e.target.checked ? '1' : null);
              pager.reset();
            }}
            data-testid="filter-include-synthetic"
          />
          <span>incluir sintéticas</span>
        </label>
        <label className="group">
          <input
            type="checkbox"
            checked={onlyLive}
            onChange={(e) => {
              setLiveFlag(e.target.checked ? '1' : null);
              pager.reset();
            }}
            data-testid="filter-only-live"
          />
          <span>solo en vivo</span>
        </label>
      </div>
      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {(query.error).message}</div>
      ) : (
        <>
          <table className="table" data-testid="sessions-table">
            <thead>
              <tr>
                <th style={{ width: 36 }} aria-label="Comparar"></th>
                <th>Started</th>
                <th>User</th>
                <th>Module</th>
                <th className="r">Duration</th>
                <th className="r">Turns</th>
                <th className="r">Cost</th>
                <th className="r">Errors</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className="muted" style={{ textAlign: 'center' }}>
                    Sin sesiones
                  </td>
                </tr>
              ) : (
                sortedItems.map((s) => {
                  const checked = compareIds.includes(s.id);
                  const disabled = !checked && compareIds.length >= MAX_COMPARE;
                  const isLive = s.endedAt === null;
                  // Para sesiones live, mostramos el elapsed contra liveNow
                  // (tick 1s). Para finalizadas, usamos totalDurationMs que
                  // viene calculado del span session en el backend.
                  const displayDurationMs = isLive
                    ? Math.max(0, liveNow - new Date(s.startedAt).getTime())
                    : s.totalDurationMs;
                  return (
                    <tr
                      key={s.id}
                      className={`row${checked ? ' is-checked' : ''}${isLive ? ' is-live' : ''}`}
                      onClick={(e) => openSession(s.id, e)}
                      data-testid={`session-row-${s.id}`}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleCompare(s.id)}
                          aria-label={`Comparar sesión ${shortId(s.id)}`}
                          data-testid={`session-compare-${s.id}`}
                        />
                      </td>
                      <td data-testid={`session-link-${s.id}`}>
                        {isLive ? (
                          <span
                            className="badge b-ok"
                            title="Sesión en curso"
                            data-testid={`session-live-${s.id}`}
                            style={{ marginRight: 6 }}
                          >
                            <span className="dot-live" /> live
                          </span>
                        ) : null}
                        {formatDateTime(s.startedAt)}
                      </td>
                      <td>
                        <Link to={`/users/${s.userId}`} onClick={(e) => e.stopPropagation()}>
                          {s.userEmail}
                        </Link>
                      </td>
                      <td className="tt-sec">{s.moduleId ?? '—'}</td>
                      <td className="r mono">{formatDurationMs(displayDurationMs)}</td>
                      <td className="r mono">{formatNumber(s.turnCount)}</td>
                      <td className="r mono">
                        {nanoToUsdCompact(preferRawCost(s.totalCostRawNano, s.totalCostNano))}
                      </td>
                      <td className="r">
                        {s.errorCount > 0 ? (
                          <span className="badge b-error">{s.errorCount}</span>
                        ) : (
                          <span className="muted">0</span>
                        )}
                      </td>
                      <td className="mono tt-tert">{shortId(s.id)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {compareIds.length > 0 ? (
            <div className="sessions-compare-bar" data-testid="sessions-compare-bar">
              <span className="sessions-compare-count">
                {compareIds.length} / {MAX_COMPARE} seleccionadas para comparar
              </span>
              <div className="sessions-compare-chips">
                {compareIds.map((id) => (
                  <span key={id} className="badge b-info mono">
                    {shortId(id, 12)}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => toggleCompare(id)}
                      aria-label="Quitar"
                      data-testid={`session-compare-remove-${id}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="btn"
                onClick={() => setCompareIds([])}
                data-testid="session-compare-clear"
              >
                Limpiar
              </button>
              <button
                type="button"
                className="btn"
                disabled={compareIds.length < 2}
                onClick={() => {
                  void navigate(`/sessions/compare?ids=${compareIds.join(',')}`);
                }}
                data-testid="session-compare-go"
              >
                Comparar →
              </button>
            </div>
          ) : null}
          <div className="flex ai-c gap-4 pad-6">
            <button
              type="button"
              className="btn"
              disabled={!pager.canGoPrev}
              onClick={pager.goPrev}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="btn"
              disabled={!query.data.nextCursor}
              onClick={() => pager.goNext(query.data.nextCursor)}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
    </>
  );
}
